import { IDataGroupDetail } from '@/pages/security/data-group/schema'
import type { AxiosRequestConfig, GenericAbortSignal } from 'axios'
import { API_ATTENDANCE_URL, API_MASTER_URL, API_ORGANIZATION_URL } from '../constants/base-urls'
import type {
  IApproval,
  IBenefit,
  IBranch,
  ICareerType,
  ICompany,
  ICostCenter,
  IDataCriteria,
  IDataGroup,
  IDataGroupPayload,
  IDepartment,
  IDocumentRequest,
  IEmailTemplate,
  IEmploymentStatus,
  IGeneralDataEmmbed,
  IGrade,
  IIdentityType,
  IImportCandidateLog,
  IInsuranceLocation,
  IJobLevels,
  ILetterTemplate,
  IOfferingLetterSetting,
  IOrganizationLevel,
  IOrganizationStructure,
  IPaginationParam,
  IPaginationResponse,
  IPosition,
  IRecruitmentStage,
  IResignReasonType,
  ISource,
  ISubGrade,
  ITaxLocation,
  ITaxParameter,
  ITaxStatus,
  IWorkplacement,
} from '../types'
import { createAxiosInstance, mergeParamsWithConfig } from '../utils'

const axios = createAxiosInstance({
  baseURL: API_ORGANIZATION_URL,
  withAuth: true,
  headers: {
    'x-api-version': 5.1,
  },
})

type FetchOrganizationParams = {
  type?: string
} & IPaginationParam

const createDynamicAxios = (baseURL: string) => {
  return createAxiosInstance({
    baseURL,
    withAuth: true,
    headers: {
      'x-api-version': 5.1,
    },
  })
}

const getApiUrl = (type: string) => {
  const apiDomains: Record<string, string> = {
    master: API_MASTER_URL,
    organization: API_ORGANIZATION_URL,
    attendance: API_ATTENDANCE_URL,
  }

  const baseUrl = apiDomains[type] || API_ORGANIZATION_URL

  return {
    baseUrl,
  }
}

/**
 * Company
 *
 */
export const fetchCompany = (oid?: string, data?: string, config?: AxiosRequestConfig) => {
  let path = '/company'
  if (oid) path += `/${oid}`
  return axios.get<{ data: ICompany }>(path, mergeParamsWithConfig({ data: data }, config)).then((response) => response.data.data)
}

export const fetchCompanies = (params?: IPaginationParam, config?: AxiosRequestConfig) => {
  return axios
    .get<{ data: IPaginationResponse<ICompany> }>(`/company/datatable`, mergeParamsWithConfig(params, config))
    .then((response) => response.data.data)
}

export const updateCompany = (oid: string, payload: Record<string, any>) => {
  return axios.put<{ data: ICompany }>(`/company/${oid}`, payload).then((response) => response.data.data)
}

/**
 * Branches
 *
 */
export const fetchBranches = (params?: { active?: boolean } & IPaginationParam, signal?: GenericAbortSignal) => {
  return axios.get<{ data: IPaginationResponse<IBranch> }>(`/branch`, { params, signal }).then((response) => response.data.data)
}

export const fetchBrance = (id: string) => {
  return axios.get<{ data: IBranch }>(`/branch/${id}`).then((response) => response.data.data)
}

export const createBranch = (payload: Record<string, any>) => {
  return axios.post<{ data: IBranch }>(`/branch`, payload).then((response) => response.data.data)
}

export const updateBranch = (id: string, payload: Record<string, any>) => {
  return axios.put<{ data: IBranch }>(`/branch/${id}`, payload).then((response) => response.data.data)
}

export const deleteBranch = (id: string) => {
  return axios.delete(`/branch/${id}`).then((response) => response.data.data)
}

/**
 * Grade
 *
 */
export const fetchGrades = (params?: IPaginationParam, signal?: GenericAbortSignal) => {
  return axios.get<{ data: IPaginationResponse<IGrade> }>(`/grade`, { params, signal }).then((response) => response.data.data)
}

export const createGrade = (payload: Record<string, any>) => {
  return axios.post<{ data: IGrade }>(`/grade`, payload).then((response) => response.data.data)
}

export const updateGrade = (id: string, payload: Record<string, any>) => {
  return axios.put<{ data: IGrade }>(`/grade/${id}`, payload).then((response) => response.data.data)
}

export const deleteGrade = (id: string) => {
  return axios.delete(`/grade/${id}`).then((response) => response.data.data)
}

export const updateOrderGrade = (id: string, payload: Record<string, any>) => {
  return axios.patch<{ data: IGrade }>(`/grade/order/${id}`, payload).then((response) => response.data.data)
}

export const downloadGrades = () => {
  return axios
    .get(`/grade/export`, {
      responseType: 'blob',
      headers: {
        Accept: 'application/octet-stream',
      },
    })
    .then((response) => {
      return response
    })
}

/**
 * Sub Grade
 *
 */
export const fetchSubGrades = (params?: IPaginationParam, signal?: GenericAbortSignal) => {
  return axios.get<{ data: IPaginationResponse<ISubGrade> }>(`/sub-grade`, { params, signal }).then((response) => response.data.data)
}

export const createSubGrade = (payload: Record<string, any>) => {
  return axios.post<{ data: ISubGrade }>(`/sub-grade`, payload).then((response) => response.data.data)
}

export const updateSubGrade = (id: string, payload: Record<string, any>) => {
  return axios.put<{ data: ISubGrade }>(`/sub-grade/${id}`, payload).then((response) => response.data.data)
}

export const deleteSubGrade = (id: string) => {
  return axios.delete(`/sub-grade/${id}`).then((response) => response.data.data)
}

/**
 * Department
 *
 */
export const fetchDepartments = (params?: IPaginationParam, signal?: GenericAbortSignal) => {
  return axios.get<{ data: IPaginationResponse<IDepartment> }>(`/department`, { params, signal }).then((response) => response.data.data)
}

export const createDepartment = (payload: Record<string, any>) => {
  return axios.post<{ data: IDepartment }>(`/department`, payload).then((response) => response.data.data)
}

export const updateDepartment = (id: string, payload: Record<string, any>) => {
  return axios.put<{ data: IDepartment }>(`/department/${id}`, payload).then((response) => response.data.data)
}

export const deleteDepartment = (id: string) => {
  return axios.delete(`/department/${id}`).then((response) => response.data.data)
}

/**
 * Benefits
 *
 */
export const fetchBenefits = (params?: IPaginationParam, signal?: GenericAbortSignal) => {
  return axios.get<{ data: IPaginationResponse<IBenefit> }>(`/benefit`, { params, signal }).then((response) => response.data.data)
}

export const createBenefit = (payload: Record<string, any>) => {
  return axios.post<{ data: IBenefit }>(`/benefit`, payload).then((response) => response.data.data)
}

export const updateBenefit = (id: string, payload: Record<string, any>) => {
  return axios.put<{ data: IBenefit }>(`/benefit/${id}`, payload).then((response) => response.data.data)
}

export const deleteBenefit = (id: string) => {
  return axios.delete(`/benefit/${id}`).then((response) => response.data.data)
}

/**
 * JobLevels
 *
 */
export const fetchJobLevels = (params?: IPaginationParam, signal?: GenericAbortSignal) => {
  return axios.get<{ data: IPaginationResponse<IJobLevels> }>(`/job-level`, { params, signal }).then((response) => response.data.data)
}

export const createJobLevel = (payload: Record<string, any>) => {
  return axios.post<{ data: IJobLevels }>(`/job-level`, payload).then((response) => response.data.data)
}

export const updateJobLevel = (id: string, payload: Record<string, any>) => {
  return axios.put<{ data: IJobLevels }>(`/job-level/${id}`, payload).then((response) => response.data.data)
}

export const updateOrderJobLevel = (id: string, payload: Record<string, any>) => {
  return axios.patch<{ data: IJobLevels }>(`/job-level/order/${id}`, payload).then((response) => response.data.data)
}

export const deleteJobLevel = (id: string) => {
  return axios.delete(`/job-level/${id}`).then((response) => response.data.data)
}

/**
 * Positions
 *
 */
export const fetchPositions = (params?: IPaginationParam, signal?: GenericAbortSignal) => {
  return axios.get<{ data: IPaginationResponse<IPosition> }>(`/position`, { params, signal }).then((response) => response.data.data)
}

export const createPosition = (payload: Record<string, any>) => {
  return axios.post<{ data: IPosition }>(`/position`, payload).then((response) => response.data.data)
}

export const updatePosition = (id: string, payload: Record<string, any>) => {
  return axios.put<{ data: IPosition }>(`/position/${id}`, payload).then((response) => response.data.data)
}

export const deletePosition = (id: string) => {
  return axios.delete(`/position/${id}`).then((response) => response.data.data)
}

/**
 * Workplacements
 *
 */
export const fetchWorkplacements = (params?: IPaginationParam, signal?: GenericAbortSignal) => {
  return axios
    .get<{ data: IPaginationResponse<IWorkplacement> }>(`/workplacement`, { params, signal })
    .then((response) => response.data.data)
}

export const createWorkplacement = (payload: Record<string, any>) => {
  return axios.post<{ data: IWorkplacement }>(`/workplacement`, payload).then((response) => response.data.data)
}

export const updateWorkplacement = (id: string, payload: Record<string, any>) => {
  return axios.put<{ data: IWorkplacement }>(`/workplacement/${id}`, payload).then((response) => response.data.data)
}

export const deleteWorkplacement = (id: string) => {
  return axios.delete(`/workplacement/${id}`).then((response) => response.data.data)
}

/**
 * JobTypes
 *
 */
export const fetchJobTypes = (
  params?: { statusEmployment?: 'ALL' | 'ACTIVE' | 'INACTIVE' } & IPaginationParam,
  signal?: GenericAbortSignal,
) => {
  return axios.get<{ data: IPaginationResponse<IEmploymentStatus> }>(`/job-type`, { params, signal }).then((response) => response.data.data)
}

export const createJobType = (payload: Record<string, any>) => {
  return axios.post<{ data: IEmploymentStatus }>(`/job-type`, payload).then((response) => response.data.data)
}

export const updateJobType = (id: string, payload: Record<string, any>) => {
  return axios.put<{ data: IEmploymentStatus }>(`/job-type/${id}`, payload).then((response) => response.data.data)
}

export const deleteJobType = (id: string) => {
  return axios.delete(`/job-type/${id}`).then((response) => response.data.data)
}

/**
 * RecruitmentStage
 *
 */
export const fetchRecruitmentStages = (params?: IPaginationParam, config: AxiosRequestConfig = {}) => {
  return axios
    .get<{ data: IPaginationResponse<IRecruitmentStage> }>(`/recruitment-stage`, { params, ...config })
    .then((response) => response.data.data)
}

export const createRecruitmentStage = (payload: Record<string, any>) => {
  return axios.post<{ data: IRecruitmentStage }>(`/recruitment-stage`, payload).then((response) => response.data.data)
}

export const updateRecruitmentStage = (id: string, payload: Record<string, any>) => {
  return axios.put<{ data: IRecruitmentStage }>(`/recruitment-stage/${id}`, payload).then((response) => response.data.data)
}

export const deleteRecruitmentStage = (id: string) => {
  return axios.delete(`/recruitment-stage/${id}`).then((response) => response.data.data)
}

/**
 * Approval
 *
 */
export const fetchApprovals = (params?: FetchOrganizationParams, signal?: GenericAbortSignal) => {
  return axios.get<{ data: IPaginationResponse<IApproval> }>(`/approval`, { params, signal }).then((response) => response.data.data)
}

export const fetchApproval = (oid: string) => {
  return axios.post<{ data: IApproval }>(`/approval/${oid}`).then((response) => response.data.data)
}

export const createApproval = (employeeCodes: string[], type: string) => {
  return axios.post<{ data: { content: IApproval[] } }>(`/approval`, { employeeCodes, type }).then((response) => response.data.data)
}

/**
 * Document Request
 *
 */
export const fetchDocumentRequests = (params?: IPaginationParam, config?: AxiosRequestConfig) => {
  return axios
    .get<{ data: IPaginationResponse<IDocumentRequest> }>(`/document-request`, { ...config, params })
    .then((response) => response.data.data)
}

export const fetchDocumentRequest = () => {
  return axios.get<{ data: IPaginationResponse<IDocumentRequest> }>(`/document-request`).then((response) => response.data.data)
}

export const createDocumentRequest = (payload: Record<string, any>) => {
  return axios.post<{ data: IDocumentRequest }>(`/document-request`, payload).then((response) => response.data.data)
}

export const updateDocumentRequest = (id: string, payload: Record<string, any>) => {
  return axios.put<{ data: IDocumentRequest }>(`/document-request/${id}`, payload).then((response) => response.data.data)
}

export const deleteDocumentRequest = (id: string) => {
  return axios.delete(`/document-request/${id}`).then((response) => response.data.data)
}

/**
 * Offering Letter
 *
 */
export const fetchOfferingLetterSetting = () => {
  return axios.get<{ data: IOfferingLetterSetting }>(`/offering-letter`).then((response) => response.data.data)
}

export const updateOfferingLetterSetting = (payload: IOfferingLetterSetting) => {
  return axios.put<{ data: IOfferingLetterSetting }>(`/offering-letter`, payload).then((response) => response.data.data)
}

export const fetchTaxLocations = (params?: IPaginationParam, signal?: GenericAbortSignal) => {
  return axios.get<{ data: IPaginationResponse<ITaxLocation> }>(`/tax-location`, { params, signal }).then((response) => response.data.data)
}

export const fetchTaxLocation = (id: string) => {
  return axios.get<{ data: ITaxLocation }>(`/tax-location/${id}`).then((response) => response.data.data)
}

export const createTaxLocation = (payload: Record<string, any>) => {
  return axios.post<{ data: ITaxLocation }>(`/tax-location`, payload).then((response) => response.data.data)
}

export const updateTaxLocation = (id: string, payload: Record<string, any>) => {
  return axios.put<{ data: ITaxLocation }>(`/tax-location/${id}`, payload).then((response) => response.data.data)
}

export const deleteTaxLocation = (id: string) => {
  return axios.delete(`/tax-location/${id}`).then((response) => response.data.data)
}

export const downloadTaxLocations = () => {
  return axios
    .get(`/tax-location/export`, {
      responseType: 'blob',
      headers: {
        Accept: 'application/octet-stream',
      },
    })
    .then((response) => {
      return response
    })
}

export const fetchInsuranceLocations = (
  params?: IPaginationParam & { typeCode?: 'bpjs_kesehatan' | 'bpjs_ketenagakerjaan' },
  signal?: GenericAbortSignal,
) => {
  return axios
    .get<{ data: IPaginationResponse<IInsuranceLocation> }>(`/insurance-location`, { params, signal })
    .then((response) => response.data.data)
}

export const fetchInsuranceLocation = (id: string) => {
  return axios.get<{ data: IInsuranceLocation }>(`/insurance-location/${id}`).then((response) => response.data.data)
}

export const createInsuranceLocation = (payload: Record<string, any>) => {
  return axios.post<{ data: IInsuranceLocation }>(`/insurance-location`, payload).then((response) => response.data.data)
}

export const updateInsuranceLocation = (id: string, payload: Record<string, any>) => {
  return axios.put<{ data: IInsuranceLocation }>(`/insurance-location/${id}`, payload).then((response) => response.data.data)
}

export const deleteInsuranceLocation = (id: string) => {
  return axios.delete(`/insurance-location/${id}`).then((response) => response.data.data)
}

export const downloadInsuranceLocations = () => {
  return axios
    .get(`/insurance-location/export`, {
      responseType: 'blob',
      headers: {
        Accept: 'application/octet-stream',
      },
    })
    .then((response) => {
      return response
    })
}

export const fetchCostCenters = (params?: IPaginationParam, signal?: GenericAbortSignal) => {
  return axios.get<{ data: IPaginationResponse<ICostCenter> }>(`/cost-center`, { params, signal }).then((response) => response.data.data)
}

export const fetchCostCenter = (id: string) => {
  return axios.get<{ data: ICostCenter }>(`/cost-center/${id}`).then((response) => response.data.data)
}

export const createCostCenter = (payload: Record<string, any>) => {
  return axios.post<{ data: ICostCenter }>(`/cost-center`, payload).then((response) => response.data.data)
}

export const updateCostCenter = (id: string, payload: Record<string, any>) => {
  return axios.put<{ data: ICostCenter }>(`/cost-center/${id}`, payload).then((response) => response.data.data)
}

export const deleteCostCenter = (id: string) => {
  return axios.delete(`/cost-center/${id}`).then((response) => response.data.data)
}

export const downloadCostCenters = () => {
  return axios
    .get(`/cost-center/export`, {
      responseType: 'blob',
      headers: {
        Accept: 'application/octet-stream',
      },
    })
    .then((response) => {
      return response
    })
}

export const fetchTaxStatuses = (params?: IPaginationParam, signal?: GenericAbortSignal) => {
  return axios.get<{ data: IPaginationResponse<ITaxStatus> }>(`/tax-status`, { params, signal }).then((response) => response.data.data)
}

export const fetchTaxStatus = (id: string) => {
  return axios.get<{ data: ITaxStatus }>(`/tax-status/${id}`).then((response) => response.data.data)
}

export const createTaxStatus = (payload: Record<string, any>) => {
  return axios.post<{ data: ITaxStatus }>(`/tax-status`, payload).then((response) => response.data.data)
}

export const updateTaxStatus = (id: string, payload: Record<string, any>) => {
  return axios.put<{ data: ITaxStatus }>(`/tax-status/${id}`, payload).then((response) => response.data.data)
}

export const deleteTaxStatus = (id: string) => {
  return axios.delete(`/tax-status/${id}`).then((response) => response.data.data)
}

export const downloadTaxStatuses = () => {
  return axios
    .get(`/tax-status/export`, {
      responseType: 'blob',
      headers: {
        Accept: 'application/octet-stream',
      },
    })
    .then((response) => {
      return response
    })
}

export const fetchTaxParameters = (params?: IPaginationParam, signal?: GenericAbortSignal) => {
  return axios
    .get<{ data: IPaginationResponse<ITaxParameter> }>(`/tax-parameter`, { params, signal })
    .then((response) => response.data.data)
}

export const fetchTaxParameter = (id: string) => {
  return axios.get<{ data: ITaxParameter }>(`/tax-parameter/${id}`).then((response) => response.data.data)
}

export const createTaxParameter = (payload: Record<string, any>) => {
  return axios.post<{ data: ITaxParameter }>(`/tax-parameter`, payload).then((response) => response.data.data)
}

export const updateTaxParameter = (id: string, payload: Record<string, any>) => {
  return axios.put<{ data: ITaxParameter }>(`/tax-parameter/${id}`, payload).then((response) => response.data.data)
}

export const deleteTaxParameter = (id: string) => {
  return axios.delete(`/tax-parameter/${id}`).then((response) => response.data.data)
}

export const downloadTaxParameters = () => {
  return axios
    .get(`/tax-parameter/export`, {
      responseType: 'blob',
      headers: {
        Accept: 'application/octet-stream',
      },
    })
    .then((response) => {
      return response
    })
}

export const fetchInsuranceTypes = (params?: IPaginationParam, signal?: GenericAbortSignal) => {
  return axios
    .get<{ data: IPaginationResponse<IGeneralDataEmmbed> }>(`/insurance-type`, { params, signal })
    .then((response) => response.data.data)
}

/**
 * Organization Level
 */
export const fetchOrganizationLevel = (params?: IPaginationParam, signal?: GenericAbortSignal) => {
  return axios
    .get<{ data: IPaginationResponse<IOrganizationLevel> }>(`/organization-level`, { params, signal })
    .then((response) => response.data.data)
}

export const downloadOrganizationLevel = () => {
  return axios.get(`/organization-level/export`).then((response) => response.data)
}

export const createOrganizationLevel = (payload: Record<string, any>) => {
  return axios.post<{ data: IOrganizationLevel }>(`/organization-level`, payload).then((response) => response.data.data)
}

export const updateOrganizationLevel = (id: string, payload: Record<string, any>) => {
  return axios.put<{ data: IOrganizationLevel }>(`/organization-level/${id}`, payload).then((response) => response.data.data)
}

export const orderOrganizationLevel = (id: string, payload: Record<string, any>) => {
  return axios.patch<{ data: IOrganizationLevel }>(`/organization-level/order/${id}`, payload).then((response) => response.data.data)
}

export const deleteOrganizationLevel = async (id: string) => {
  return axios.delete(`/organization-level/${id}`).then((response) => response.data.data)
}

/**
 * Organization Structure
 */
export const fetchOrganizationStructure = (params?: IPaginationParam, signal?: GenericAbortSignal) => {
  return axios
    .get<{ data: IPaginationResponse<IOrganizationStructure> }>(`/organization-structure`, { params, signal })
    .then((response) => response.data.data)
}

export const fetchOrganizationStructureById = (id: string) => {
  return axios.get<{ data: IOrganizationStructure }>(`/organization-structure/${id}`).then((response) => response.data.data)
}

export const createOrganizationStructure = (payload: Record<string, any>) => {
  return axios.post<{ data: IOrganizationStructure }>(`/organization-structure`, payload).then((response) => response.data.data)
}

export const updateOrganizationStructure = (id: string, payload: Record<string, any>) => {
  return axios.put<{ data: IOrganizationStructure }>(`/organization-structure/${id}`, payload).then((response) => response.data.data)
}

export const deleteOrganizationStructure = (id: string) => {
  return axios.delete(`/organization-structure/${id}`).then((response) => response.data.data)
}

export const downloadOrganizationStructure = () => {
  return axios
    .get(`/organization-structure/export`, {
      responseType: 'blob',
      headers: {
        Accept: 'application/octet-stream',
      },
    })
    .then((response) => {
      return response
    })
}

export const fetchDataCriteria = (params?: IPaginationParam, signal?: GenericAbortSignal) => {
  return axios
    .get<{ data: IPaginationResponse<IDataCriteria> }>(`/data-criteria`, { params, signal })
    .then((response) => response.data.data)
}

export const fetchByPath = async (params?: IPaginationParam & { path: string; type: string }, signal?: GenericAbortSignal) => {
  if (!params?.path) {
    throw new Error('Path is required')
  }

  if (!params?.type) {
    throw new Error('Type is required')
  }

  const finalParams: IPaginationParam = {
    limit: params.limit,
    page: params.page,
    q: params.q,
    sortDirection: params.sortDirection,
    sortedField: params.sortedField,
  }

  const { baseUrl } = getApiUrl(params.type)

  const dynamicAxios = createDynamicAxios(baseUrl)

  return dynamicAxios
    .get<{ data: IPaginationResponse<IGeneralDataEmmbed> }>(params.path, {
      signal,
      params: {
        ...finalParams,
      },
    })
    .then((response) => response.data.data)
}

/**
 * Data Group
 *
 */
export const fetchDataGroups = (params?: { active?: boolean; roleGroupId?: string } & IPaginationParam, signal?: GenericAbortSignal) => {
  return axios.get<{ data: IPaginationResponse<IDataGroup> }>(`/data-group`, { params, signal }).then((response) => response.data.data)
}

export const fetchDataGroup = (oid: string) => {
  return axios.get<{ data: IDataGroupDetail }>(`/data-group/${oid}`).then((response) => response.data.data)
}

export const createDataGroup = (payload: Record<string, any>) => {
  return axios.post<{ data: IDataGroupPayload }>(`/data-group`, payload).then((response) => response.data.data)
}

export const updateDataGroup = (oid: string, payload: Record<string, any>) => {
  return axios.put<{ data: IDataGroupPayload }>(`/data-group/${oid}`, payload).then((response) => response.data.data)
}

export const deleteDataGroup = (oid: string) => {
  return axios.delete(`/data-group/${oid}`).then((response) => response.data.data)
}

/**
 * Career Type
 *
 */
export const fetchCareerTypes = (params?: IPaginationParam, config?: AxiosRequestConfig) => {
  return axios
    .get<{ data: IPaginationResponse<ICareerType> }>(`/career-type`, mergeParamsWithConfig(params, config))
    .then((response) => response.data.data)
}

/**
 * Reason Type
 *
 */
export const fetchResignReasonTypes = (params?: IPaginationParam, config?: AxiosRequestConfig) => {
  return axios
    .get<{ data: IPaginationResponse<IResignReasonType> }>(`/resign-reason-type`, mergeParamsWithConfig(params, config))
    .then((response) => response.data.data)
}

/**
 * Letter Template
 *
 */
export const fetchLetterTemplates = (params?: IPaginationParam, config?: AxiosRequestConfig) => {
  return axios
    .get<{ data: IPaginationResponse<ILetterTemplate> }>(`/letter-template`, mergeParamsWithConfig(params, config))
    .then((response) => response.data.data)
}

export const fetchLetterTemplate = (oid: string) => {
  return axios.get<{ data: ILetterTemplate }>(`/letter-template/${oid}`).then((response) => response.data.data)
}

export const createLetterTemplate = (payload: Record<string, any>) => {
  return axios.post<{ data: ILetterTemplate }>(`/letter-template`, payload).then((response) => response.data.data)
}

export const updateLetterTemplate = (oid: string, payload: Record<string, any>) => {
  return axios.put<{ data: ILetterTemplate }>(`/letter-template/${oid}`, payload).then((response) => response.data.data)
}

export const deleteLetterTemplate = (oid: string) => {
  return axios.delete(`/letter-template/${oid}`).then((response) => response.data.data)
}

/**
 * Email Template
 *
 */
export const fetchEmailTemplates = (params?: IPaginationParam, config?: AxiosRequestConfig) => {
  return axios
    .get<{ data: IPaginationResponse<IEmailTemplate> }>(`/email-template`, mergeParamsWithConfig(params, config))
    .then((response) => response.data.data)
}

export const fetchEmailTemplateTypes = (params?: IPaginationParam, config?: AxiosRequestConfig) => {
  return axios
    .get<{ data: IPaginationResponse<IEmailTemplate> }>(`/email-template-type`, mergeParamsWithConfig(params, config))
    .then((response) => response.data.data)
}

export const createEmailTemplate = (payload: Record<string, any>) => {
  return axios.post<{ data: IEmailTemplate }>(`/email-template`, payload).then((response) => response.data.data)
}

export const updateEmailTemplate = (oid: string, payload: Record<string, any>) => {
  return axios.put<{ data: IEmailTemplate }>(`/email-template/${oid}`, payload).then((response) => response.data.data)
}

export const deleteEmailTemplate = (oid: string) => {
  return axios.delete(`/email-template/${oid}`).then((response) => response.data.data)
}

export const fetchEmailTemplate = (oid: string) => {
  return axios.get<{ data: IEmailTemplate }>(`/email-template/${oid}`).then((response) => response.data.data)
}

export const fetchIdentityTypes = (params?: IPaginationParam, config?: AxiosRequestConfig) => {
  return axios
    .get<{ data: IPaginationResponse<IIdentityType> }>('identity-type', mergeParamsWithConfig(params, config))
    .then((response) => response.data.data)
}

export const fetchIdentityType = (oid: string) => {
  return axios.get<{ data: IIdentityType }>(`identity-type/${oid}`).then((response) => response.data.data)
}

export const createIdentityType = (payload: Record<string, any>) => {
  return axios.post<{ data: IIdentityType }>(`/identity-type`, payload).then((response) => response.data.data)
}

export const updateIdentityType = (oid: string, payload: Record<string, any>) => {
  return axios.put<{ data: IIdentityType }>(`/identity-type/${oid}`, payload).then((response) => response.data.data)
}

export const deleteIdentityType = (oid: string) => {
  return axios.delete(`/identity-type/${oid}`).then((response) => response.data.data)
}

export const fetchTaxTypes = (params?: IPaginationParam, config?: AxiosRequestConfig) => {
  return axios
    .get<{ data: IPaginationResponse<IGeneralDataEmmbed> }>(`/tax-type`, mergeParamsWithConfig(params, config))
    .then((response) => response.data.data)
}

export const fetchTaxSettingTypes = (params?: IPaginationParam, config?: AxiosRequestConfig) => {
  return axios
    .get<{ data: IPaginationResponse<IGeneralDataEmmbed> }>(`/tax-setting-type`, mergeParamsWithConfig(params, config))
    .then((response) => response.data.data)
}

/**
 * Source
 *
 */
export const fetchSources = (params?: IPaginationParam, signal?: GenericAbortSignal) => {
  return axios.get<{ data: IPaginationResponse<ISource> }>(`/source`, { params, signal }).then((response) => response.data.data)
}

export const createSource = (payload: Record<string, any>) => {
  return axios.post<{ data: ISource }>(`/source`, payload).then((response) => response.data.data)
}

export const updateSource = (id: string, payload: Record<string, any>) => {
  return axios.put<{ data: ISource }>(`/source/${id}`, payload).then((response) => response.data.data)
}

export const deleteSource = (id: string) => {
  return axios.delete(`/source/${id}`).then((response) => response.data.data)
}

/**
 * Import Candidate
 *
 */
export const fetchImportTemplateUrl = (type: 'CANDIDATE') => {
  return axios.get<{ data: { url: string } }>(`/import-template`, { params: { type } }).then((response) => response.data.data)
}

export const getLogImportCandidate = (params?: IPaginationParam) => {
  return axios.get<{ data: IPaginationResponse<IImportCandidateLog> }>(`/import-log`, { params }).then((response) => response.data.data)
}
